import React from "react";
import {
    Button,
    NumberInput,
    ScrollArea,
    Table,
    Text,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconTrash } from "@tabler/icons-react";

import {
    formatMoney,
} from "../../utils/hotelBookingUtils";

import {
    BookingRoomRow,
    HotelBookingTexts,
} from "./HotelBookingTypes";

type Props = {
    rooms: BookingRoomRow[];
    onRemoveRoom: (id: string) => void;

    onUpdateRoom?: (
        id: string,
        changes: Partial<BookingRoomRow>
    ) => void;

    editable?: boolean;

    minDate?: Date | null;
    maxDate?: Date | null;

    t: HotelBookingTexts;
};

export default function RoomBookingTable({
    rooms,
    onRemoveRoom,
    onUpdateRoom,
    editable = false,
    minDate,
    maxDate,
    t,
}: Props) {
    return (
        <ScrollArea>
        <Table
            withTableBorder
            striped
            highlightOnHover
        >
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>{t.roomType}</Table.Th>
                    <Table.Th>{t.checkIn}</Table.Th>
                    <Table.Th>{t.checkOut}</Table.Th>
                    <Table.Th>{t.guests}</Table.Th>
                    <Table.Th>{t.priceNight}</Table.Th>
                    <Table.Th>{t.nights}</Table.Th>
                    <Table.Th>{t.total}</Table.Th>
                    <Table.Th>{t.actions}</Table.Th>
                </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
                {rooms.length === 0 ? (
                    <Table.Tr>
                        <Table.Td colSpan={9}>
                            <Text
                                c="dimmed"
                                size="sm"
                            >
                                {t.noRooms}
                            </Text>
                        </Table.Td>
                    </Table.Tr>
                ) : (
                    rooms.map((row, index) => (
                        <Table.Tr key={row.id}>
                            <Table.Td>
                                {index + 1}
                            </Table.Td>

                            <Table.Td>
                                {row.roomTypeName}
                            </Table.Td>

                            <Table.Td>
                                {editable && onUpdateRoom ? (
                                    <DateInput
                                        value={row.checkInDate}
                                        minDate={
                                            minDate ??
                                            undefined
                                        }
                                        maxDate={
                                            maxDate ??
                                            undefined
                                        }
                                        onChange={(value) => {
                                            if (!value) return;

                                            onUpdateRoom(
                                                row.id,
                                                {
                                                    checkInDate:
                                                        value,
                                                }
                                            );
                                        }}
                                        w={145}
                                    />
                                ) : (
                                    row.checkInDate.toLocaleDateString(
                                        "da-DK"
                                    )
                                )}
                            </Table.Td>

                            <Table.Td>
                                {editable && onUpdateRoom ? (
                                    <DateInput
                                        value={row.checkOutDate}
                                        minDate={
                                            row.checkInDate
                                        }
                                        maxDate={
                                            maxDate ??
                                            undefined
                                        }
                                        onChange={(value) => {
                                            if (!value) return;

                                            onUpdateRoom(
                                                row.id,
                                                {
                                                    checkOutDate:
                                                        value,
                                                }
                                            );
                                        }}
                                        w={145}
                                    />
                                ) : (
                                    row.checkOutDate.toLocaleDateString(
                                        "da-DK"
                                    )
                                )}
                            </Table.Td>

                            <Table.Td>
                                {editable && onUpdateRoom ? (
                                    <NumberInput
                                        value={
                                            row.guestsCount
                                        }
                                        min={1}
                                        max={10}
                                        onChange={(value) =>
                                            onUpdateRoom(
                                                row.id,
                                                {
                                                    guestsCount:
                                                        Number(
                                                            value ||
                                                            1
                                                        ),
                                                }
                                            )
                                        }
                                        w={90}
                                    />
                                ) : (
                                    row.guestsCount
                                )}
                            </Table.Td>

                            <Table.Td>
                                {formatMoney(
                                    row.pricePerNight
                                )}
                            </Table.Td>

                            <Table.Td>
                                {row.nights}
                            </Table.Td>

                            <Table.Td>
                                {formatMoney(
                                    row.totalPrice
                                )}
                            </Table.Td>

                            <Table.Td>
                                <Button
                                    size="xs"
                                    color="red"
                                    variant="subtle"
                                    onClick={() =>
                                        onRemoveRoom(
                                            row.id
                                        )
                                    }
                                >
                                    <IconTrash
                                        size={15}
                                    />
                                </Button>
                            </Table.Td>
                        </Table.Tr>
                    ))
                )}
            </Table.Tbody>
            </Table>
        </ScrollArea>
    );
}